import React from 'react';
import {
  Box,
  Grid,
  Typography,
  Stack,
  Button,
  Divider,
  Card,
  CardContent,
  CircularProgress,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Pagination,
  Autocomplete,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Link,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Tooltip,
} from '@mui/material';
import InsertChartOutlinedIcon from '@mui/icons-material/InsertChartOutlined';
import CommentOutlinedIcon from '@mui/icons-material/CommentOutlined';
import { useAuth } from '../../contexts/AuthContext';
import ReportTransactionIssueDialog from '../../components/portal/ReportTransactionIssueDialog';
import { buildOrderLineItemLabel } from '../../utils/orderLineItemLabel';
import { get } from '../../utils/api';
import {
  FilterButton,
  Stat,
  CloseX,
  ignoreBackdropEscapeClose,
  portalTableContainerSx,
  portalTableHeadCellSx,
  portalTableBodyRowSx,
} from '../../components/portal/portalUi';
import PortalPageHeader from '../../components/portal/PortalPageHeader';

// Donations shows only actual completed contributions. Pledge records
// (pledge-pending / pledge-completed) are commitments, not payments, and live
// on the Pledges page — never list or total them here.
const DONATION_INCLUDED_STATUSES = ['completed'];
const DONATION_INCLUDED_STATUS_PARAM = DONATION_INCLUDED_STATUSES.join(',');

function isIncludedDonationStatus(status) {
  return DONATION_INCLUDED_STATUSES.includes(String(status || '').toLowerCase());
}

function formatCurrencySafe(value, currency = 'USD') {
  try {
    return new Intl.NumberFormat(undefined, { style: 'currency', currency }).format(Number(value || 0));
  } catch {
    return `$${Number(value || 0).toFixed(2)}`;
  }
}

function formatCurrencyNoCents(value, currency = 'USD') {
  try {
    return new Intl.NumberFormat(undefined, {
      style: 'currency',
      currency,
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(Number(value || 0));
  } catch {
    return `$${Math.round(Number(value || 0)).toLocaleString()}`;
  }
}

function orderCreatedAt(order) {
  return new Date(order?.date_created || order?.date_created_gmt || 0);
}

function startOfDayIso(dateText) {
  return new Date(`${dateText}T00:00:00.000Z`).toISOString();
}

function endOfDayIso(dateText) {
  return new Date(`${dateText}T23:59:59.999Z`).toISOString();
}

function monthKey(date) {
  return `${date.getUTCFullYear()}-${String(date.getUTCMonth() + 1).padStart(2, '0')}`;
}

function dayKey(date) {
  return date.toISOString().slice(0, 10);
}

function yearKey(date) {
  return String(date.getUTCFullYear());
}

function labelFromKey(key, bucket) {
  if (bucket === 'year') return key;
  if (bucket === 'day') {
    try {
      return new Date(`${key}T00:00:00.000Z`).toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
    } catch {
      return key;
    }
  }
  const [y, m] = String(key).split('-').map(Number);
  if (!y || !m) return key;
  try {
    return new Date(Date.UTC(y, m - 1, 1)).toLocaleDateString(undefined, { month: 'short', year: 'numeric' });
  } catch {
    return key;
  }
}

function buildSmoothPath(points) {
  if (!points.length) return '';
  if (points.length === 1) return `M ${points[0].x} ${points[0].y}`;
  let path = `M ${points[0].x} ${points[0].y}`;
  for (let i = 0; i < points.length - 1; i += 1) {
    const p0 = points[i - 1] || points[i];
    const p1 = points[i];
    const p2 = points[i + 1];
    const p3 = points[i + 2] || p2;
    const cp1x = p1.x + (p2.x - p0.x) / 6;
    const cp1y = p1.y + (p2.y - p0.y) / 6;
    const cp2x = p2.x - (p3.x - p1.x) / 6;
    const cp2y = p2.y - (p3.y - p1.y) / 6;
    path += ` C ${cp1x} ${cp1y}, ${cp2x} ${cp2y}, ${p2.x} ${p2.y}`;
  }
  return path;
}

function buildAreaPath(points, baselineY) {
  if (!points.length) return '';
  return `${buildSmoothPath(points)} L ${points[points.length - 1].x} ${baselineY} L ${points[0].x} ${baselineY} Z`;
}

function buildLineItemLabel(li) {
  if (!li) return '';
  let lineName = String(li.name || '').replace(/^Select:\s*/i, '').trim();
  let variation = String(li.variation_label || li.variation_name || li.variation || '').replace(/^Select:\s*/i, '').trim();
  if (variation && lineName.includes(variation)) variation = '';
  return variation ? `${lineName} — ${variation}` : lineName;
}

function splitLineItemLabel(label) {
  const raw = String(label || '').trim();
  if (!raw) return { primary: '', secondary: '' };
  const parts = raw.split(' — ');
  if (parts.length <= 1) return { primary: raw, secondary: '' };
  return {
    primary: parts.shift() || raw,
    secondary: parts.join(' — '),
  };
}

function matchesAnalyticsRange(order, rangeKey, customStart, customEnd) {
  const dt = orderCreatedAt(order);
  if (Number.isNaN(dt.getTime())) return false;
  const now = new Date();
  const year = now.getUTCFullYear();
  const startTodayUtc = Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate());
  const orderUtc = Date.UTC(dt.getUTCFullYear(), dt.getUTCMonth(), dt.getUTCDate());
  if (rangeKey === 'all') return true;
  if (rangeKey === 'ytd' || rangeKey === 'current_year') {
    return dt.getUTCFullYear() === year;
  }
  if (rangeKey === 'last_year') {
    return dt.getUTCFullYear() === year - 1;
  }
  if (rangeKey === '1y') {
    const cutoff = new Date(startTodayUtc);
    cutoff.setUTCMonth(cutoff.getUTCMonth() - 12);
    return orderUtc >= Date.UTC(cutoff.getUTCFullYear(), cutoff.getUTCMonth(), cutoff.getUTCDate());
  }
  if (rangeKey === 'custom') {
    if (!customStart || !customEnd) return false;
    const start = Date.parse(`${customStart}T00:00:00.000Z`);
    const end = Date.parse(`${customEnd}T23:59:59.999Z`);
    return orderUtc >= start && orderUtc <= end;
  }
  return true;
}

function buildTrendSeries(orders, rangeKey, customStart, customEnd) {
  const filtered = orders.filter((o) => matchesAnalyticsRange(o, rangeKey, customStart, customEnd));
  let bucket = 'month';
  if (rangeKey === 'all') bucket = 'year';
  if (rangeKey === 'custom' && customStart && customEnd) {
    const start = Date.parse(`${customStart}T00:00:00.000Z`);
    const end = Date.parse(`${customEnd}T23:59:59.999Z`);
    const daySpan = Math.max(1, Math.ceil((end - start) / (24 * 60 * 60 * 1000)));
    bucket = daySpan <= 45 ? 'day' : 'month';
  }
  const sums = new Map();
  filtered.forEach((o) => {
    const dt = orderCreatedAt(o);
    const key = bucket === 'year' ? yearKey(dt) : bucket === 'day' ? dayKey(dt) : monthKey(dt);
    sums.set(key, (sums.get(key) || 0) + Number(o.total_num || o.total || 0));
  });
  return Array.from(sums.entries())
    .sort((a, b) => a[0].localeCompare(b[0]))
    .map(([key, value]) => ({ key, label: labelFromKey(key, bucket), value: Number(value || 0) }));
}

function summarizeAnalytics(orders, rangeKey, customStart, customEnd) {
  const filtered = orders.filter((o) => matchesAnalyticsRange(o, rangeKey, customStart, customEnd));
  const total = filtered.reduce((sum, o) => sum + Number(o.total_num || o.total || 0), 0);
  const txCount = filtered.length;
  const avg = txCount ? total / txCount : 0;
  const series = buildTrendSeries(filtered, rangeKey === 'all' ? 'all' : rangeKey, customStart, customEnd);
  const peakPoint = [...series].sort((a, b) => b.value - a.value)[0] || null;
  const byProduct = new Map();
  filtered.forEach((o) => {
    const items = Array.isArray(o.line_items) ? o.line_items : [];
    items.forEach((li) => {
      const label = buildLineItemLabel(li) || 'Donation';
      const key = `${li.product_id || 'p'}:${li.variation_id || 0}:${label}`;
      if (!byProduct.has(key)) {
        byProduct.set(key, { key, label, amount: 0, count: 0, quantity: 0 });
      }
      const row = byProduct.get(key);
      row.amount += Number(li.total_num ?? li.total ?? 0);
      row.count += 1;
      row.quantity += Math.max(1, Number(li.quantity || 1));
    });
  });
  const topByAmount = [...byProduct.values()]
    .sort((a, b) => (b.amount - a.amount) || (b.count - a.count) || a.label.localeCompare(b.label))
    .slice(0, 5);
  const topByCount = [...byProduct.values()]
    .sort((a, b) => (b.count - a.count) || (b.amount - a.amount) || a.label.localeCompare(b.label))
    .slice(0, 5);
  const yearTotals = [...buildTrendSeries(orders, 'all', customStart, customEnd)]
    .sort((a, b) => Number(b.key) - Number(a.key));
  return {
    filtered,
    total,
    txCount,
    avg,
    series,
    peakPoint,
    topByAmount,
    topByCount,
    yearTotals,
  };
}

function DonationsTrendChart({ series, currency }) {
  const width = 640;
  const height = 250;
  const padX = 28;
  const padTop = 18;
  const padBottom = 40;
  const chartHeight = height - padTop - padBottom;
  const chartWidth = width - padX * 2;
  const values = series.map((p) => Number(p.value || 0));
  const max = Math.max(...values, 0);
  const safeMax = max > 0 ? max : 1;
  const points = series.map((p, idx) => ({
    x: padX + (series.length === 1 ? chartWidth / 2 : (idx * chartWidth) / Math.max(series.length - 1, 1)),
    y: padTop + chartHeight - ((Number(p.value || 0) / safeMax) * chartHeight),
    ...p,
  }));
  const areaPath = buildAreaPath(points, padTop + chartHeight);
  const linePath = buildSmoothPath(points);

  if (!series.length) {
    return (
      <Box
        sx={{
          height: 250,
          borderRadius: 2,
          border: '1px dashed',
          borderColor: 'divider',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          color: 'text.secondary',
        }}
      >
        <Typography variant="body2">No donation data in this timeline.</Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ width: '100%' }}>
      <svg viewBox={`0 0 ${width} ${height}`} width="100%" height={height} role="img" aria-label="Donation trend chart">
        <defs>
          <linearGradient id="donationArea" x1="0" x2="0" y1="0" y2="1">
            <stop offset="0%" stopColor="#1a237e" stopOpacity="0.3" />
            <stop offset="100%" stopColor="#1a237e" stopOpacity="0.04" />
          </linearGradient>
        </defs>
        {[0, 0.25, 0.5, 0.75, 1].map((tick) => {
          const y = padTop + chartHeight - (tick * chartHeight);
          const val = safeMax * tick;
          return (
            <g key={`tick-${tick}`}>
              <line x1={padX} x2={padX + chartWidth} y1={y} y2={y} stroke="rgba(26,35,126,0.12)" strokeDasharray="4 4" />
                <text x={padX - 8} y={y + 4} textAnchor="end" fontSize="11" fill="#6b7280">
                  {formatCurrencyNoCents(val, currency)}
                </text>
              </g>
          );
        })}
        <path d={areaPath} fill="url(#donationArea)" />
        <path d={linePath} fill="none" stroke="#1a237e" strokeWidth="3" strokeLinecap="round" />
        {points.map((p) => (
          <g key={p.key}>
            <circle cx={p.x} cy={p.y} r="4.5" fill="#ff6d00" stroke="#fff" strokeWidth="2" />
            <text x={p.x} y={height - 14} textAnchor="middle" fontSize="11" fill="#4a4a4a">
              {p.label}
            </text>
          </g>
        ))}
      </svg>
    </Box>
  );
}

export default function PortalDonationsPage() {
  const { staff } = useAuth();
  const ready = !!staff?.subject;

  const [range, setRange] = React.useState('1y');
  const [orders, setOrders] = React.useState([]);
  const [ordersLoading, setOrdersLoading] = React.useState(false);
  const [ordersError, setOrdersError] = React.useState('');
  const [ordersMeta, setOrdersMeta] = React.useState(null);
  const [orderPage, setOrderPage] = React.useState(1);
  const pageSize = 25;
  const [years, setYears] = React.useState([]);
  const [selectedYear, setSelectedYear] = React.useState('');
  const [productQuery, setProductQuery] = React.useState('');
  const [productOptions, setProductOptions] = React.useState([]);
  const [productLoading, setProductLoading] = React.useState(false);
  const [selectedProduct, setSelectedProduct] = React.useState(null);
  const [customStart, setCustomStart] = React.useState('');
  const [customEnd, setCustomEnd] = React.useState('');

  const [statsLoading, setStatsLoading] = React.useState(false);
  const [lifetimeTotal, setLifetimeTotal] = React.useState(null);
  const [ytdTotal, setYtdTotal] = React.useState(null);
  const [lastYearTotal, setLastYearTotal] = React.useState(null);
  const [threeYearsTotal, setThreeYearsTotal] = React.useState(null);
  const [lifetimeCount, setLifetimeCount] = React.useState(0);
  const [lifeMemberDialogOpen, setLifeMemberDialogOpen] = React.useState(false);
  const [issueOrder, setIssueOrder] = React.useState(null);
  const [analyticsOpen, setAnalyticsOpen] = React.useState(false);
  const [analyticsLoading, setAnalyticsLoading] = React.useState(false);
  const [analyticsError, setAnalyticsError] = React.useState('');
  const [analyticsOrders, setAnalyticsOrders] = React.useState([]);
  const [analyticsRange, setAnalyticsRange] = React.useState('1y');
  const [analyticsCustomStart, setAnalyticsCustomStart] = React.useState('');
  const [analyticsCustomEnd, setAnalyticsCustomEnd] = React.useState('');

  const lifeMemberEligible = (Number(threeYearsTotal) || 0) >= 30000;

  const analyticsSummary = React.useMemo(
    () => summarizeAnalytics(analyticsOrders, analyticsRange, analyticsCustomStart, analyticsCustomEnd),
    [analyticsOrders, analyticsRange, analyticsCustomStart, analyticsCustomEnd]
  );

  const periodTotal = React.useMemo(() => {
    try {
      const included = (orders || []).filter((o) => isIncludedDonationStatus(o.status));
      const sum = included.reduce((acc, o) => acc + (parseFloat(o.total) || 0), 0);
      return new Intl.NumberFormat(undefined, { style: 'currency', currency: (orders?.[0]?.currency || 'USD') }).format(sum);
    } catch {
      const included = (orders || []).filter((o) => isIncludedDonationStatus(o.status));
      const sum = included.reduce((acc, o) => acc + (parseFloat(o.total) || 0), 0);
      return `$${sum.toFixed(2)}`;
    }
  }, [orders]);

  const lifetimeAvgFmt = React.useMemo(() => {
    try {
      if (!lifetimeCount) return '$0.00';
      const currency = (orders?.[0]?.currency || 'USD');
      return new Intl.NumberFormat(undefined, { style: 'currency', currency }).format((Number(lifetimeTotal || 0)) / lifetimeCount);
    } catch {
      if (!lifetimeCount) return '$0.00';
      const avg = (Number(lifetimeTotal || 0)) / lifetimeCount;
      return `$${avg.toFixed(2)}`;
    }
  }, [lifetimeTotal, lifetimeCount, orders]);

  const sumCompleted = React.useCallback(async ({ range: r = 'all', start, end }) => {
    if (!ready) return 0;
    const per_page = 100;
    let page = 1;
    let total = 0;
    while (true) {
      const params = { status: DONATION_INCLUDED_STATUS_PARAM, page, per_page };
      if (r === 'custom') { params.start = start; params.end = end; }
      const resp = await get('/portal/orders', params);
      const batch = Array.isArray(resp?.data) ? resp.data : [];
      total += batch.reduce((s, o) => s + (Number(o.total_num) || 0), 0);
      if (batch.length < per_page) break;
      page += 1;
      if (page > 50) break;
    }
    return total;
  }, [ready]);

  const sumCompletedWithCount = React.useCallback(async ({ range: r = 'all', start, end }) => {
    if (!ready) return { total: 0, count: 0 };
    const per_page = 100;
    let page = 1;
    let total = 0;
    let count = 0;
    while (true) {
      const params = { status: DONATION_INCLUDED_STATUS_PARAM, page, per_page };
      if (r === 'custom') { params.start = start; params.end = end; }
      const resp = await get('/portal/orders', params);
      const batch = Array.isArray(resp?.data) ? resp.data : [];
      total += batch.reduce((s, o) => s + (Number(o.total_num) || 0), 0);
      count += batch.length;
      if (batch.length < per_page) break;
      page += 1;
      if (page > 50) break;
    }
    return { total, count };
  }, [ready]);

  const loadAnalyticsOrders = React.useCallback(async () => {
    if (!ready) return;
    setAnalyticsLoading(true);
    setAnalyticsError('');
    try {
      const per_page = 100;
      let page = 1;
      const rawOrders = [];
      while (page <= 50) {
        const resp = await get('/portal/orders', { status: DONATION_INCLUDED_STATUS_PARAM, page, per_page });
        const batch = Array.isArray(resp?.data) ? resp.data : [];
        rawOrders.push(...batch);
        if (batch.length < per_page) break;
        page += 1;
      }
      const detailed = await Promise.all(rawOrders.map(async (o) => {
        try {
          const det = await get(`/portal/orders/${o.id}`);
          const line_items = Array.isArray(det?.items) ? det.items.map((it) => ({
            id: it.item_id || it.id,
            name: it.name,
            product_id: it.product_id,
            variation_id: it.variation_id,
            quantity: it.quantity,
            total_num: Number(it.total_num ?? it.price_num ?? 0),
          })) : [];
          return {
            ...o,
            total_num: Number(o.total_num || 0),
            date_created: o.date_created_gmt || o.date_created,
            line_items,
          };
        } catch {
          return {
            ...o,
            total_num: Number(o.total_num || 0),
            date_created: o.date_created_gmt || o.date_created,
            line_items: [],
          };
        }
      }));
      const productIds = Array.from(new Set(detailed.flatMap((ord) => (ord.line_items || []).map((li) => li.product_id)).filter(Boolean)));
      let variationLabelById = new Map();
      if (productIds.length > 0) {
        try {
          const resVars = await get('/portal/facets/variations', { product_id: productIds.join(',') });
          const vars = Array.isArray(resVars?.variations) ? resVars.variations : [];
          variationLabelById = new Map(vars.map((v) => {
            const attrs = Array.isArray(v.attributes) ? v.attributes : [];
            const label = attrs.map((a) => {
              const name = (a.name || a.attribute || '').toString();
              const val = (a.option || a.value || '').toString();
              return name && val ? `${name}: ${val}` : (val || name);
            }).filter(Boolean).join(', ');
            return [v.id, label || String(v.display_name || v.sku || '').trim()];
          }));
        } catch {
          variationLabelById = new Map();
        }
      }
      setAnalyticsOrders(detailed.map((o) => ({
        ...o,
        line_items: (o.line_items || []).map((li) => ({
          ...li,
          variation_label: li.variation_id ? variationLabelById.get(li.variation_id) || '' : '',
        })),
      })));
    } catch (e) {
      setAnalyticsError(e?.message || 'Could not load donation analytics.');
      setAnalyticsOrders([]);
    } finally {
      setAnalyticsLoading(false);
    }
  }, [ready]);

  React.useEffect(() => {
    let cancelled = false;
    (async () => {
      if (!ready) return;
      setStatsLoading(true);
      try {
        const now = new Date();
        const year = now.getFullYear();
        const startYtd = `${year}-01-01`;
        const endYtd = now.toISOString().slice(0, 10);
        const prevYear = year - 1;
        const startLastYear = `${prevYear}-01-01`;
        const endLastYear = `${prevYear}-12-31`;
        const start3 = `${year - 2}-01-01`;
        const end3 = endYtd;
        const [{ total: lifeTotal, count: lifeCount }, ytd, lastYr, three] = await Promise.all([
          sumCompletedWithCount({ range: 'all' }),
          sumCompleted({ range: 'custom', start: startYtd, end: endYtd }),
          sumCompleted({ range: 'custom', start: startLastYear, end: endLastYear }),
          sumCompleted({ range: 'custom', start: start3, end: end3 }),
        ]);
        if (!cancelled) {
          setLifetimeTotal(lifeTotal);
          setLifetimeCount(lifeCount);
          setYtdTotal(ytd);
          setLastYearTotal(lastYr);
          setThreeYearsTotal(three);
        }
      } catch {
        if (!cancelled) {
          setLifetimeTotal(0); setYtdTotal(0); setLastYearTotal(0); setThreeYearsTotal(0);
        }
      } finally {
        if (!cancelled) setStatsLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [ready, sumCompleted, sumCompletedWithCount]);

  React.useEffect(() => {
    if (!ready) return;
    let cancelled = false;
    (async () => {
      try {
        let page = 1;
        const per_page = 100;
        const yrs = new Set();
        while (page <= 5) {
          const resp = await get('/portal/orders', { status: DONATION_INCLUDED_STATUS_PARAM, page, per_page });
          const batch = Array.isArray(resp?.data) ? resp.data : [];
          for (const o of batch) {
            const d = o.date_created_gmt || o.date_created;
            if (d) yrs.add(String(new Date(d).getFullYear()));
          }
          if (batch.length < per_page) break;
          page += 1;
        }
        if (!cancelled) setYears(Array.from(yrs).sort((a, b) => Number(b) - Number(a)));
      } catch {
        if (!cancelled) setYears([]);
      }
    })();
    return () => { cancelled = true; };
  }, [ready]);

  React.useEffect(() => {
    if (!ready) return;
    if (range === 'custom' && (!customStart || !customEnd)) return;

    let cancelled = false;
    const fetchOrders = async () => {
      setOrdersLoading(true);
      setOrdersError('');
      try {
        const params = {};
        params.status = DONATION_INCLUDED_STATUS_PARAM;
        if (range === 'custom') {
          params.start = new Date(`${customStart}T00:00:00.000Z`).toISOString();
          params.end = new Date(`${customEnd}T23:59:59.999Z`).toISOString();
        }
        if (selectedYear) {
          params.start = new Date(`${selectedYear}-01-01T00:00:00.000Z`).toISOString();
          params.end = new Date(`${selectedYear}-12-31T23:59:59.999Z`).toISOString();
        } else if (range === 'custom') {
          params.start = new Date(`${customStart}T00:00:00.000Z`).toISOString();
          params.end = new Date(`${customEnd}T23:59:59.999Z`).toISOString();
        }
        const hasProductSearch = (selectedProduct && selectedProduct.id) || (productQuery && productQuery.trim().length >= 3);
        let endpoint = '/portal/orders';
        if (hasProductSearch) {
          endpoint = '/portal/orders/search';
          delete params.start;
          delete params.end;
          if (selectedProduct && selectedProduct.id) {
            params.product_ids = String(selectedProduct.id);
          } else {
            params.product = productQuery.trim();
          }
        }
        params.page = orderPage;
        params.per_page = pageSize;
        const res = await get(endpoint, params);
        if (cancelled) return;
        const arr = Array.isArray(res?.data) ? res.data : [];
        const mapped = arr.map((o) => ({
          id: o.id,
          status: o.status,
          currency: o.currency || 'USD',
          total: String(o.total_num || 0),
          date_created: o.date_created_gmt || o.date_created,
          billing_first_name: o.billing_first_name,
          billing_last_name: o.billing_last_name,
          billing_email: o.billing_email,
        }));
        const detailed = await Promise.all(mapped.map(async (o) => {
          try {
            const det = await get(`/portal/orders/${o.id}`);
            const line_items = Array.isArray(det?.items) ? det.items.map((it) => ({
              id: it.item_id || it.id,
              name: it.name,
              product_id: it.product_id,
              variation_id: it.variation_id,
              quantity: it.quantity,
              total: String(it.total_num ?? it.price_num ?? 0),
            })) : [];
            return { ...o, line_items };
          } catch {
            return { ...o, line_items: [] };
          }
        }));
        try {
          const productIds = Array.from(new Set(detailed.flatMap((ord) => (ord.line_items || []).map((li) => li.product_id)).filter(Boolean)));
          let variationLabelById = new Map();
          if (productIds.length > 0) {
            const resVars = await get('/portal/facets/variations', { product_id: productIds.join(',') });
            const vars = Array.isArray(resVars?.variations) ? resVars.variations : [];
            variationLabelById = new Map(vars.map((v) => {
              const attrs = Array.isArray(v.attributes) ? v.attributes : [];
              const label = attrs.map((a) => {
                const name = (a.name || a.attribute || '').toString();
                const val = (a.option || a.value || '').toString();
                return name && val ? `${name}: ${val}` : (val || name);
              }).filter(Boolean).join(', ');
              return [v.id, label || (v.sku || '')];
            }));
          }
          for (const ord of detailed) {
            if (Array.isArray(ord.line_items)) {
              ord.line_items = ord.line_items.map((li) => ({
                ...li,
                variation_label: variationLabelById.get(li.variation_id) || '',
              }));
            }
          }
        } catch { /* ignore */ }
        setOrders(detailed);
        setOrdersMeta({ total: res?.meta?.total || detailed.length });
      } catch (e) {
        if (!cancelled) setOrdersError(e?.message || 'Failed to load orders');
      } finally {
        if (!cancelled) setOrdersLoading(false);
      }
    };
    fetchOrders();
    return () => { cancelled = true; };
  }, [ready, range, customStart, customEnd, selectedYear, selectedProduct, productQuery, orderPage]);

  React.useEffect(() => {
    if (!analyticsOpen || analyticsOrders.length > 0 || analyticsLoading || !ready) return;
    loadAnalyticsOrders();
  }, [analyticsOpen, analyticsOrders.length, analyticsLoading, ready, loadAnalyticsOrders]);

  const loadProductOptions = React.useCallback(async (q) => {
    if (!q || q.trim().length < 3) { setProductOptions([]); return; }
    try {
      setProductLoading(true);
      const res = await get('/portal/facets/products', { q });
      setProductOptions(Array.isArray(res?.products) ? res.products : []);
    } catch {
      setProductOptions([]);
    } finally {
      setProductLoading(false);
    }
  }, []);

  const currencyFmt = new Intl.NumberFormat(undefined, { style: 'currency', currency: (orders?.[0]?.currency || 'USD') });
  const analyticsCurrency = orders?.[0]?.currency || analyticsOrders?.[0]?.currency || 'USD';
  const statLifetimeSx = {
    bgcolor: 'rgba(25, 118, 210, 0.07)',
    borderColor: 'rgba(25, 118, 210, 0.18)',
  };
  const statPeriodSx = {
    bgcolor: 'rgba(255, 152, 0, 0.1)',
    borderColor: 'rgba(237, 108, 2, 0.2)',
  };
  const statInsightsSx = {
    bgcolor: 'rgba(26, 35, 126, 0.08)',
    borderColor: 'rgba(26, 35, 126, 0.22)',
    cursor: 'pointer',
    transition: 'transform 120ms ease, box-shadow 120ms ease, background-color 120ms ease',
    '&:hover': {
      transform: 'translateY(-1px)',
      boxShadow: 2,
      bgcolor: 'rgba(26, 35, 126, 0.12)',
    },
  };

  return (
    <>
      <PortalPageHeader title="Donations" />
      <Card variant="outlined" sx={{ borderRadius: 2 }}>
        <CardContent>
          <Grid container spacing={2}>
            <Grid item xs={6} sm={4} md={2} sx={{ display: 'flex', minWidth: 0 }}>
              <Stat label="Transactions Count" value={String(lifetimeCount)} help="Transactions (lifetime)" sx={statLifetimeSx} />
            </Grid>
            <Grid item xs={6} sm={4} md={2} sx={{ display: 'flex', minWidth: 0 }}>
              <Stat label="Avg Donation" value={lifetimeAvgFmt.replace(/\.00$/, '')} help="Avg per transaction" sx={statLifetimeSx} />
            </Grid>
            <Grid item xs={6} sm={4} md={2} sx={{ display: 'flex', minWidth: 0 }}>
              <Stat label="Total Donations" value={formatCurrencyNoCents(lifetimeTotal || 0, orders?.[0]?.currency || 'USD')} help="All-time" sx={statLifetimeSx} />
            </Grid>
            <Grid item xs={6} sm={4} md={2} sx={{ display: 'flex', minWidth: 0 }}>
              <Stat label="This Year" value={formatCurrencyNoCents(ytdTotal || 0, orders?.[0]?.currency || 'USD')} help="YTD contributions" sx={statPeriodSx} />
            </Grid>
            <Grid item xs={6} sm={4} md={2} sx={{ display: 'flex', minWidth: 0 }}>
              <Stat
                label="Last Year"
                value={formatCurrencyNoCents(lastYearTotal || 0, orders?.[0]?.currency || 'USD')}
                help={`Donations in ${new Date().getFullYear() - 1}`}
                sx={statPeriodSx}
              />
            </Grid>
            <Grid item xs={6} sm={4} md={2} sx={{ display: 'flex', minWidth: 0 }}>
              <Stat
                label="Last 3 Years"
                value={formatCurrencyNoCents(threeYearsTotal || 0, orders?.[0]?.currency || 'USD')}
                help="Donations in last 3 years"
                sx={statPeriodSx}
              />
            </Grid>
            <Grid item xs={6} sm={4} md={2} sx={{ display: 'flex', minWidth: 0 }}>
              <Card
                variant="outlined"
                onClick={() => setAnalyticsOpen(true)}
                sx={{ height: '100%', width: '100%', ...statInsightsSx }}
              >
                <CardContent>
                  <Typography variant="overline" color="text.secondary">Giving Insights</Typography>
                  <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 0.5 }}>
                    <InsertChartOutlinedIcon sx={{ color: 'primary.main', fontSize: 30 }} />
                    <Typography variant="h6" sx={{ fontWeight: 700 }}>Analytics</Typography>
                  </Stack>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
          {lifeMemberEligible && (
            <Typography variant="body2" color="text.secondary" sx={{ mt: 2, maxWidth: 560, lineHeight: 1.5 }}>
              You are eligible for becoming life member of ICCT.{' '}
              <Link
                component="button"
                type="button"
                variant="body2"
                onClick={() => setLifeMemberDialogOpen(true)}
                sx={{ verticalAlign: 'baseline', fontWeight: 600, cursor: 'pointer' }}
              >
                Click here for more details
              </Link>
              .
            </Typography>
          )}
          {statsLoading && (
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>Updating summary…</Typography>
          )}

          <Divider sx={{ my: 2 }} />

          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2, gap: 2, flexWrap: 'wrap' }}>
            <Stack direction="row" spacing={1} alignItems="center" sx={{ flexWrap: 'wrap' }}>
              <FilterButton active={range === 'all'} onClick={() => { setRange('all'); setSelectedYear(''); setSelectedProduct(null); setProductQuery(''); setOrderPage(1); }}>All</FilterButton>
              <FilterButton active={range === '1y'} onClick={() => { setRange('1y'); setSelectedYear(''); setSelectedProduct(null); setProductQuery(''); setOrderPage(1); }}>Last 12 mon</FilterButton>
              <FilterButton active={range === 'last_year'} onClick={() => { setRange('last_year'); setSelectedYear(''); setSelectedProduct(null); setProductQuery(''); setOrderPage(1); }}>Last year</FilterButton>
              <FilterButton active={range === 'ytd'} onClick={() => { setRange('ytd'); setSelectedYear(''); setSelectedProduct(null); setProductQuery(''); setOrderPage(1); }}>YTD</FilterButton>
              <FilterButton
                active={range === 'custom'}
                onClick={() => {
                  setRange('custom');
                  setSelectedYear('');
                  setSelectedProduct(null);
                  setProductQuery('');
                  if (!customStart || !customEnd) {
                    const today = new Date();
                    const end = today.toISOString().slice(0, 10);
                    const s = new Date(today);
                    s.setDate(today.getDate() - 30);
                    const start = s.toISOString().slice(0, 10);
                    setCustomStart(start);
                    setCustomEnd(end);
                  }
                }}
              >
                Custom
              </FilterButton>
              <FormControl size="small" sx={{ minWidth: 140 }}>
                <InputLabel id="year-filter-label">Year</InputLabel>
                <Select
                  labelId="year-filter-label"
                  label="Year"
                  value={selectedYear}
                  onChange={(e) => { setSelectedYear(e.target.value); setRange(''); setSelectedProduct(null); setProductQuery(''); setOrderPage(1); }}
                >
                  <MenuItem value=""><em>All</em></MenuItem>
                  {years.map((y) => (<MenuItem key={y} value={y}>{y}</MenuItem>))}
                </Select>
              </FormControl>
              {range === 'custom' && (
                <Stack direction="row" spacing={2} alignItems="center" sx={{ ml: 1 }}>
                  <Box>
                    <Typography variant="caption" color="text.secondary">From</Typography>
                    <input
                      type="date"
                      value={customStart}
                      onChange={(e) => setCustomStart(e.target.value)}
                      style={{ height: 32, padding: '4px 8px', borderRadius: 4, border: '1px solid rgba(0,0,0,0.23)' }}
                    />
                  </Box>
                  <Box>
                    <Typography variant="caption" color="text.secondary">To</Typography>
                    <input
                      type="date"
                      value={customEnd}
                      onChange={(e) => setCustomEnd(e.target.value)}
                      style={{ height: 32, padding: '4px 8px', borderRadius: 4, border: '1px solid rgba(0,0,0,0.23)' }}
                    />
                  </Box>
                </Stack>
              )}
            </Stack>
            <Box sx={{ minWidth: 320, maxWidth: 480, flex: 1 }}>
              <Autocomplete
                size="small"
                options={productOptions}
                loading={productLoading}
                value={selectedProduct}
                onChange={(_, v) => { setSelectedProduct(v); setOrderPage(1); }}
                onInputChange={(_, v) => { setProductQuery(v); if ((v || '').trim().length >= 3) loadProductOptions(v); }}
                getOptionLabel={(o) => (o ? `${o.name || ''}${o.sku ? `  ·  ${o.sku}` : ''}` : '')}
                renderInput={(params) => (
                  <TextField {...params} label="Search donations (3+ chars)" placeholder="Type to search donations" />
                )}
                isOptionEqualToValue={(opt, val) => String(opt.id) === String(val?.id)}
                clearOnBlur={false}
              />
            </Box>
          </Box>

          {ordersLoading && (
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', py: 6 }}>
              <Box sx={{ textAlign: 'center', '@keyframes pulse': { '0%': { opacity: 0.4 }, '50%': { opacity: 1 }, '100%': { opacity: 0.4 } } }}>
                <CircularProgress size={56} sx={{ mb: 2 }} />
                <Typography variant="h5" sx={{ fontWeight: 800, letterSpacing: 0.5, animation: 'pulse 1.6s ease-in-out infinite' }}>
                  Retrieving orders...
                </Typography>
                <Typography variant="body2" color="text.secondary">Please wait a moment</Typography>
              </Box>
            </Box>
          )}

          {!ordersLoading && (() => {
            const fmt = (d) => {
              try { return new Date(d).toLocaleDateString(); } catch { return null; }
            };
            let period = '';
            const after = ordersMeta?.after || null;
            const before = ordersMeta?.before || null;
            if (range === 'all' || (!after && !before)) {
              period = 'All time';
            } else {
              const from = after ? fmt(after) : '...';
              const to = before ? fmt(before) : fmt(new Date().toISOString());
              period = `${from} to ${to}`;
            }
            return (
              <Stack direction="row" spacing={2} alignItems="center" justifyContent="space-between" sx={{ mb: 1, flexWrap: 'wrap' }}>
                <Stack direction="row" spacing={2} alignItems="baseline" sx={{ flexWrap: 'wrap' }}>
                  <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>Orders</Typography>
                  <Typography variant="body2" color="text.secondary">{period}</Typography>
                  <Typography variant="body2" color="text.secondary"> ·  Total contributions: {periodTotal}</Typography>
                </Stack>
                {(ordersMeta?.total > pageSize) && (
                  <Pagination
                    count={Math.ceil((ordersMeta?.total || 0) / pageSize)}
                    page={orderPage}
                    onChange={(_, p) => setOrderPage(p)}
                    size="small"
                  />
                )}
              </Stack>
            );
          })()}
          {ordersError && (
            <Typography variant="body2" color="error">{ordersError}</Typography>
          )}
          {!ordersLoading && !ordersError && (
            orders.length === 0 ? (
              <Typography variant="body2" color="text.secondary">No orders found for selected range.</Typography>
            ) : (
              (() => {
                const sorted = [...orders].sort((a, b) => {
                  const da = new Date(a.date_created || a.date_created_gmt || 0).getTime();
                  const db = new Date(b.date_created || b.date_created_gmt || 0).getTime();
                  return db - da;
                });
                const lowerQ = (productQuery || '').trim().toLowerCase();
                const filtered = (() => {
                  if (selectedProduct && selectedProduct.id) {
                    return sorted.filter((o) => (o.line_items || []).some((li) => String(li.product_id || '') === String(selectedProduct.id)));
                  } if (lowerQ.length >= 3) {
                    return sorted.filter((o) => (o.line_items || []).some((li) =>
                      String(li.name || '').toLowerCase().includes(lowerQ) || String(li.sku || '').toLowerCase().includes(lowerQ)
                    ));
                  }
                  return sorted;
                })();
                const currency = orders?.[0]?.currency || 'USD';
                const fmtTotal = (o) => {
                  try {
                    return new Intl.NumberFormat(undefined, { style: 'currency', currency }).format(Number(o.total_num ?? o.total ?? 0));
                  } catch {
                    return `$${Number(o.total_num ?? o.total ?? 0).toFixed(2)}`;
                  }
                };
                return (
                  <Box>
                    <TableContainer component={Paper} variant="outlined" sx={portalTableContainerSx}>
                      <Table size="small" stickyHeader sx={{ minWidth: 640 }}>
                        <TableHead>
                          <TableRow>
                            <TableCell sx={portalTableHeadCellSx}>Tx #</TableCell>
                            <TableCell sx={portalTableHeadCellSx}>Date</TableCell>
                            <TableCell sx={portalTableHeadCellSx}>Status</TableCell>
                            <TableCell sx={portalTableHeadCellSx}>Product</TableCell>
                            <TableCell align="right" sx={portalTableHeadCellSx}>Total</TableCell>
                            <TableCell align="center" sx={{ ...portalTableHeadCellSx, width: 48, px: 0.5 }} aria-label="Comment" />
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {filtered.map((o, idx) => {
                            const pending = String(o.status || '').toLowerCase() === 'pledge-pending';
                            return (
                              <TableRow key={o.id} hover sx={portalTableBodyRowSx(idx)}>
                                <TableCell sx={{ fontFamily: 'monospace', fontSize: '0.875rem' }}>{o.id}</TableCell>
                                <TableCell>{new Date(o.date_created || o.date_created_gmt).toLocaleDateString()}</TableCell>
                                <TableCell sx={{ color: pending ? '#800000' : 'inherit' }}>
                                  <Typography variant="body2" sx={{ textTransform: 'capitalize' }}>{o.status}</Typography>
                                </TableCell>
                                <TableCell sx={{ maxWidth: 360 }}>
                                  {(Array.isArray(o.line_items) ? o.line_items : []).map((li, liIdx) => (
                                    <Typography
                                      key={`${o.id}-li-${li.product_id || 'p'}-${li.variation_id || 'v'}-${liIdx}`}
                                      variant="body2"
                                      sx={{ lineHeight: 1.45 }}
                                    >
                                      {buildOrderLineItemLabel(li)}
                                    </Typography>
                                  ))}
                                </TableCell>
                                <TableCell align="right">{fmtTotal(o)}</TableCell>
                                <TableCell align="center" sx={{ width: 48, px: 0.5 }}>
                                  <Tooltip title="Comment on this transaction">
                                    <IconButton
                                      size="small"
                                      color="primary"
                                      aria-label={`Comment on transaction ${o.id}`}
                                      onClick={() => setIssueOrder(o)}
                                    >
                                      <CommentOutlinedIcon fontSize="small" />
                                    </IconButton>
                                  </Tooltip>
                                </TableCell>
                              </TableRow>
                            );
                          })}
                        </TableBody>
                      </Table>
                    </TableContainer>
                    {(ordersMeta?.total > pageSize) && (
                      <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2 }}>
                        <Pagination
                          count={Math.ceil((ordersMeta?.total || 0) / pageSize)}
                          page={orderPage}
                          onChange={(_, p) => setOrderPage(p)}
                          size="small"
                        />
                      </Box>
                    )}
                  </Box>
                );
              })()
            )
          )}
        </CardContent>
      </Card>

      <Dialog
        open={lifeMemberDialogOpen}
        onClose={(e, reason) => {
          if (ignoreBackdropEscapeClose(e, reason)) return;
          setLifeMemberDialogOpen(false);
        }}
        disableEscapeKeyDown
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', pr: 1 }}>
          <Typography variant="h6" component="span">Life membership — ICCT</Typography>
          <CloseX onClick={() => setLifeMemberDialogOpen(false)} />
        </DialogTitle>
        <DialogContent dividers>
          <Typography variant="body2" color="text.secondary">
            Eligibility criteria, guidelines, and next steps for life membership will be published here.
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
            Content is coming soon.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button variant="contained" onClick={() => setLifeMemberDialogOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={analyticsOpen}
        onClose={(e, reason) => {
          if (ignoreBackdropEscapeClose(e, reason)) return;
          setAnalyticsOpen(false);
        }}
        disableEscapeKeyDown
        maxWidth="lg"
        fullWidth
      >
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', pr: 1 }}>
          <Typography variant="h6" component="span">Giving Insights</Typography>
          <CloseX onClick={() => setAnalyticsOpen(false)} />
        </DialogTitle>
        <DialogContent dividers>
          <Stack spacing={2.5}>
            <Typography variant="body2" color="text.secondary">
              Explore donation trends, top supported products, repeat giving patterns, and year-wise contributions.
            </Typography>

            <Stack direction="row" spacing={1} alignItems="center" sx={{ flexWrap: 'wrap' }}>
              <FilterButton active={analyticsRange === '1y'} onClick={() => setAnalyticsRange('1y')}>Last 12 mon</FilterButton>
              <FilterButton active={analyticsRange === 'current_year'} onClick={() => setAnalyticsRange('current_year')}>Current year</FilterButton>
              <FilterButton active={analyticsRange === 'last_year'} onClick={() => setAnalyticsRange('last_year')}>Last year</FilterButton>
              <FilterButton active={analyticsRange === 'ytd'} onClick={() => setAnalyticsRange('ytd')}>YTD</FilterButton>
              <FilterButton
                active={analyticsRange === 'custom'}
                onClick={() => {
                  setAnalyticsRange('custom');
                  if (!analyticsCustomStart || !analyticsCustomEnd) {
                    const today = new Date();
                    const end = today.toISOString().slice(0, 10);
                    const s = new Date(today);
                    s.setUTCDate(today.getUTCDate() - 30);
                    setAnalyticsCustomStart(s.toISOString().slice(0, 10));
                    setAnalyticsCustomEnd(end);
                  }
                }}
              >
                Custom
              </FilterButton>
              <FilterButton active={analyticsRange === 'all'} onClick={() => setAnalyticsRange('all')}>All time</FilterButton>
            </Stack>

            {analyticsRange === 'custom' ? (
              <Stack direction="row" spacing={2} alignItems="center" sx={{ flexWrap: 'wrap' }}>
                <Box>
                  <Typography variant="caption" color="text.secondary">From</Typography>
                  <input
                    type="date"
                    value={analyticsCustomStart}
                    onChange={(e) => setAnalyticsCustomStart(e.target.value)}
                    style={{ height: 32, padding: '4px 8px', borderRadius: 4, border: '1px solid rgba(0,0,0,0.23)' }}
                  />
                </Box>
                <Box>
                  <Typography variant="caption" color="text.secondary">To</Typography>
                  <input
                    type="date"
                    value={analyticsCustomEnd}
                    onChange={(e) => setAnalyticsCustomEnd(e.target.value)}
                    style={{ height: 32, padding: '4px 8px', borderRadius: 4, border: '1px solid rgba(0,0,0,0.23)' }}
                  />
                </Box>
              </Stack>
            ) : null}

            {analyticsLoading ? (
              <Box sx={{ py: 6, display: 'flex', justifyContent: 'center' }}>
                <CircularProgress />
              </Box>
            ) : analyticsError ? (
              <Typography variant="body2" color="error">{analyticsError}</Typography>
            ) : (
              <>
                <Box
                  sx={{
                    display: 'grid',
                    gridTemplateColumns: { xs: '1fr', md: 'minmax(0, 1fr) 320px' },
                    gap: 2,
                    alignItems: 'stretch',
                  }}
                >
                  <Box sx={{ minWidth: 0 }}>
                    <Paper variant="outlined" sx={{ p: 2, borderRadius: 2, height: '100%' }}>
                      <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 0.5 }}>Donation Trend</Typography>
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                        Smooth trend line across the selected timeline.
                      </Typography>
                      <DonationsTrendChart series={analyticsSummary.series} currency={analyticsCurrency} />
                    </Paper>
                  </Box>
                  <Box>
                    <Grid container spacing={2}>
                      <Grid item xs={6} md={6}>
                        <Stat label="Timeline Total" value={formatCurrencyNoCents(analyticsSummary.total, analyticsCurrency)} help="Selected range" sx={statLifetimeSx} />
                      </Grid>
                      <Grid item xs={6} md={6}>
                        <Stat label="Transactions" value={String(analyticsSummary.txCount)} help="Included orders" sx={statLifetimeSx} />
                      </Grid>
                      <Grid item xs={6} md={6}>
                        <Stat label="Avg Donation" value={formatCurrencyNoCents(analyticsSummary.avg, analyticsCurrency)} help="Average per transaction" sx={statLifetimeSx} />
                      </Grid>
                      <Grid item xs={6} md={6}>
                        <Stat
                          label="Peak Period"
                          value={analyticsSummary.peakPoint ? formatCurrencyNoCents(analyticsSummary.peakPoint.value, analyticsCurrency) : '$0'}
                          help={analyticsSummary.peakPoint ? analyticsSummary.peakPoint.label : 'No donations yet'}
                          sx={statLifetimeSx}
                        />
                      </Grid>
                    </Grid>
                  </Box>
                </Box>

                <Box
                  sx={{
                    display: 'grid',
                    gridTemplateColumns: { xs: '1fr', md: 'minmax(0, 1fr) 320px' },
                    gap: 2,
                    alignItems: 'stretch',
                  }}
                >
                  <Box sx={{ minWidth: 0 }}>
                    <Paper variant="outlined" sx={{ p: 2, borderRadius: 2, height: '100%' }}>
                      <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1.5 }}>Top Supported Products</Typography>
                      {analyticsSummary.topByAmount.length === 0 ? (
                        <Typography variant="body2" color="text.secondary">No product-level data in this timeline.</Typography>
                      ) : (
                        <Stack spacing={1.25}>
                          {analyticsSummary.topByAmount.map((row, idx) => (
                            (() => {
                              const label = splitLineItemLabel(row.label);
                              return (
                                <Box key={row.key} sx={{ display: 'flex', justifyContent: 'space-between', gap: 2, alignItems: 'flex-start' }}>
                                  <Box sx={{ minWidth: 0, flex: 1 }}>
                                    <Typography variant="body2" sx={{ fontWeight: 500, lineHeight: 1.4 }}>
                                      {idx + 1}. {label.primary}
                                    </Typography>
                                    {label.secondary ? (
                                      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.25, lineHeight: 1.35 }}>
                                        {label.secondary}
                                      </Typography>
                                    ) : null}
                                  </Box>
                                  <Typography variant="body2" color="text.secondary" sx={{ whiteSpace: 'nowrap', pt: 0.15 }}>
                                    {formatCurrencyNoCents(row.amount, analyticsCurrency)}
                                  </Typography>
                                </Box>
                              );
                            })()
                          ))}
                        </Stack>
                      )}
                    </Paper>
                  </Box>
                  <Box sx={{ display: { xs: 'none', md: 'block' } }} />
                </Box>

                {analyticsRange === 'all' ? (
                  <Box
                    sx={{
                      display: 'grid',
                      gridTemplateColumns: { xs: '1fr', md: 'minmax(0, 1fr) 320px' },
                      gap: 2,
                      alignItems: 'stretch',
                    }}
                  >
                    <Box sx={{ minWidth: 0 }}>
                      <Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
                        <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1.5 }}>Year-wise Contributions</Typography>
                        <TableContainer>
                          <Table size="small">
                            <TableHead>
                              <TableRow>
                                <TableCell sx={portalTableHeadCellSx}>Year</TableCell>
                                <TableCell align="right" sx={portalTableHeadCellSx}>Total</TableCell>
                              </TableRow>
                            </TableHead>
                            <TableBody>
                              {analyticsSummary.yearTotals.map((row, idx) => (
                                <TableRow key={row.key} sx={portalTableBodyRowSx(idx)}>
                                  <TableCell>{row.label}</TableCell>
                                  <TableCell align="right">{formatCurrencyNoCents(row.value, analyticsCurrency)}</TableCell>
                                </TableRow>
                              ))}
                            </TableBody>
                          </Table>
                        </TableContainer>
                      </Paper>
                    </Box>
                    <Box sx={{ display: { xs: 'none', md: 'block' } }} />
                  </Box>
                ) : null}
              </>
            )}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button variant="contained" onClick={() => setAnalyticsOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      <ReportTransactionIssueDialog
        open={!!issueOrder}
        order={issueOrder}
        onClose={() => setIssueOrder(null)}
      />
    </>
  );
}
